<template>
   <ClientOnly>
      <Teleport to="body">
         <AnimatePresence>
            <!-- Backdrop -->
            <motion.div
               v-if="model"
               data-name="modal-backdrop"
               :initial="{ opacity: 0 }"
               :animate="{ opacity: 1 }"
               :exit="{ opacity: 0 }"
               :transition="{ duration: 0.2, ease: 'easeInOut' }"
               class="fixed inset-0 z-50 bg-black/30"
               @mousedown="handleBackdropMouseDown"
               @mouseup="handleBackdropMouseUp"
            />
         </AnimatePresence>

         <AnimatePresence>
            <!-- Modal content -->
            <motion.div
               v-if="model"
               data-name="modal-content"
               :initial="isMobile ? { y: '100%', opacity: 1 } : { opacity: 0, y: 8 }"
               :animate="isMobile ? { y: 0, opacity: 1 } : { opacity: 1, y: 0 }"
               :exit="isMobile ? { y: '100%', opacity: 0 } : { opacity: 0, y: 8 }"
               :transition="
                  isMobile
                     ? { type: 'spring', damping: 30, mass: 0.8, stiffness: 300 }
                     : { duration: 0.2, ease: 'easeInOut' }
               "
               class="md:px-(--dashboard-page-padding) pointer-events-none fixed inset-0 z-50 flex items-end justify-center md:items-center"
            >
               <div
                  class="__shadow md:rounded-base pointer-events-auto w-full bg-white max-md:rounded-t-2xl"
                  :class="{
                     'max-w-[900px]': !size || size === 'xl',
                     'max-w-[800px]': size === 'lg',
                     'max-w-[700px]': size === 'md',
                     'max-w-[500px]': size === 'sm',
                     'max-w-fit': size === 'fit',
                  }"
                  @click.stop
               >
                  <Skeleton :loading="loading" width="100%" height="100%">
                     <div
                        class="flex w-full flex-col"
                        ref="modalContainer"
                        :class="{
                           'max-h-[92dvh] md:max-h-[800px]': !size || size === 'xl',
                           'min-h-[92dvh] md:min-h-[800px]': (!size || size === 'xl') && !fitHeight,
                           'max-h-[92dvh] md:max-h-[700px]': size === 'lg',
                           'md:min-h-[700px]': size === 'lg' && !fitHeight,
                           'max-h-[92dvh] md:max-h-[600px]': size === 'md',
                           'md:min-h-[600px]': size === 'md' && !fitHeight,
                           'max-h-[92dvh]': size === 'sm' || size === 'fit',
                        }"
                     >
                        <div class="shrink-0 pt-5">
                           <!-- Title row: title + close -->
                           <div class="flex items-start justify-between gap-2 px-4">
                              <TitleDescription
                                 v-if="title || description"
                                 :title="title"
                                 :description="description"
                                 truncate
                                 class="min-w-0"
                              />

                              <Icon
                                 name="close"
                                 class="__clickable text-grey size-6 shrink-0"
                                 @click="handleClose"
                              />
                           </div>

                           <!-- Actions + extra, inline -->
                           <div
                              v-if="
                                 currentCancelButton ||
                                 currentConfirmButton ||
                                 currentExtraButton ||
                                 extra ||
                                 $slots['extra-header']
                              "
                              class="mt-2 flex min-h-10 gap-2 px-4 max-md:flex-col md:items-center"
                           >
                              <div class="flex gap-2">
                                 <HighlightedText
                                    v-if="extra"
                                    :uppercase="false"
                                    type="light-blue"
                                    :text="extra"
                                 />
                                 <slot name="extra-header" />
                              </div>

                              <div
                                 v-if="
                                    currentCancelButton ||
                                    currentConfirmButton ||
                                    currentExtraButton
                                 "
                                 class="flex items-center gap-2 md:ml-auto"
                              >
                                 <Button
                                    v-if="currentExtraButton"
                                    @click="currentExtraButton.action"
                                    :disabled="currentExtraButton.disabled"
                                    :loading="currentExtraButton.loading"
                                    :iconBefore="currentExtraButton.icon"
                                    variant="light-grey"
                                 >
                                    {{ currentExtraButton.text }}
                                 </Button>
                                 <Button
                                    v-if="currentCancelButton"
                                    @click="currentCancelButton.action"
                                    :disabled="currentCancelButton.disabled"
                                    :loading="currentCancelButton.loading"
                                    :iconBefore="currentCancelButton.icon"
                                    variant="light-grey"
                                 >
                                    {{ currentCancelButton.text }}
                                 </Button>
                                 <Button
                                    v-if="currentConfirmButton"
                                    @click="currentConfirmButton.action"
                                    :disabled="currentConfirmButton.disabled"
                                    :loading="currentConfirmButton.loading"
                                    :iconBefore="currentConfirmButton.icon"
                                 >
                                    {{ currentConfirmButton.text }}
                                 </Button>
                              </div>
                           </div>

                           <!-- Tabs -->
                           <div v-if="tabs" class="mt-3">
                              <Tabs v-model="modalTab" :tabs :disabled="disabledTabs" bleed />
                           </div>

                           <!-- Header slot -->
                           <div v-if="$slots['header']" class="mt-3 px-4">
                              <slot name="header" />
                           </div>

                           <div
                              class="bg-light-grey mt-3 h-[1px] w-full transition-opacity duration-300"
                              :class="isScrolled ? 'opacity-100' : 'opacity-0'"
                           />
                        </div>

                        <div
                           ref="scrollContainer"
                           class="flex grow flex-col overflow-y-auto px-4 pb-10 pt-4"
                           @scroll="handleScroll"
                        >
                           <slot />
                        </div>
                     </div>
                  </Skeleton>
               </div>
            </motion.div>
         </AnimatePresence>
      </Teleport>
   </ClientOnly>
</template>

<script setup lang="ts">
import HighlightedText from '../core/HighlightedText.vue'
import type { Tabs } from '../core/Tabs.vue'

import { AnimatePresence, motion } from 'motion-v'
import { computed, ref, watch } from 'vue'

type Btn = {
   text: string
   icon?: string
   action?: () => void
   disabled?: boolean
   loading?: boolean
}

type Props = {
   title?: string
   description?: string
   extra?: string
   loading?: boolean
   tabs?: Tabs
   disabledTabs?: boolean | boolean[]
   confirmButton?: Btn | (Btn | undefined)[]
   cancelButton?: Btn | (Btn | undefined)[]
   extraButton?: Btn | (Btn | undefined)[]
   size?: 'xl' | 'lg' | 'md' | 'sm' | 'fit'
   fitHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
   size: 'xl',
})
const { $isBreakpoint } = useNuxtApp()
const isMobile = computed(() => $isBreakpoint('md', 'smaller'))
const modalContainer = ref<HTMLElement>()

const model = defineModel<boolean>({
   default: true,
})
const modalTab = defineModel<number>('tab', {
   default: 0,
})
const mouseDownOnBackdrop = ref(false)

const currentConfirmButton = computed(() => {
   if (Array.isArray(props.confirmButton)) {
      return props.confirmButton[modalTab.value]
   }
   return props.confirmButton
})

const currentCancelButton = computed(() => {
   if (Array.isArray(props.cancelButton)) {
      return props.cancelButton[modalTab.value]
   }
   return props.cancelButton
})

const currentExtraButton = computed(() => {
   if (Array.isArray(props.extraButton)) {
      return props.extraButton[modalTab.value]
   }
   return props.extraButton
})

const isSubmitting = computed(() => {
   return !!(
      currentConfirmButton.value?.loading ||
      currentCancelButton.value?.loading ||
      currentExtraButton.value?.loading
   )
})

const handleClose = () => {
   if (isSubmitting.value) return
   model.value = false
}

const handleBackdropMouseDown = () => {
   mouseDownOnBackdrop.value = true
}

const handleBackdropMouseUp = () => {
   if (mouseDownOnBackdrop.value) {
      handleClose()
   }
   mouseDownOnBackdrop.value = false
}

const scrollContainer = ref<HTMLElement>()
const isScrolled = ref(false)

const handleScroll = (event: Event) => {
   const target = event.target as HTMLElement
   const scrollTop = target.scrollTop
   isScrolled.value = scrollTop > 0
}

watch(model, (newValue) => {
   if (!newValue) {
      setTimeout(() => {
         modalTab.value = 0
      }, 200)
   }
})

watch(modalTab, () => {
   isScrolled.value = false
   if (scrollContainer.value) {
      scrollContainer.value.scrollTop = 0
   }
})
</script>
